import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/AuthContext";
import { ProtectedRoute, PublicRoute, StaffRoute } from "./components/ProtectedRoute";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSetup from "./pages/AdminSetup";
import Dashboard from "./pages/Dashboard";
import StaffDashboard from "./pages/StaffDashboard";
import StaffSettings from "./pages/StaffSettings";
import StaffAccount from "./pages/StaffAccount";
import Employees from "./pages/Employees";
import Shifts from "./pages/Shifts";
import Breaks from "./pages/Breaks";
import Absences from "./pages/Absences";
import Events from "./pages/Events";
import Reports from "./pages/Reports";
import Charts from "./pages/Charts";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Documentation from "./pages/Documentation";
import Account from "./pages/Account";
import BusinessHours from "./pages/BusinessHours";
import Layout from "./components/Layout";
import StaffLayout from "./components/StaffLayout";
import { Toaster } from "./components/ui/sonner";

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem("theme");
    return stored || "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App min-h-screen bg-background text-foreground">
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicRoute allowAuthenticated><Landing /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/admin-setup" element={<AdminSetup />} />
              <Route path="/documentation" element={<Documentation />} />
              
              {/* Platform Admin Route */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requirePlatformAdmin>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Payment Success Route */}
              <Route 
                path="/payment-success" 
                element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Company Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="employees" element={<Employees />} />
                <Route path="shifts" element={<Shifts />} />
                <Route path="breaks" element={<Breaks />} />
                <Route path="absences" element={<Absences />} />
                <Route path="events" element={<Events />} />
                <Route path="reports" element={<Reports />} />
                <Route path="reports/charts" element={<Charts />} />
                <Route path="settings" element={<Settings />} />
                <Route path="billing" element={<Billing />} />
                <Route path="account" element={<Account />} />
                <Route path="business-hours" element={<BusinessHours />} />
              </Route>
              
              {/* Staff Routes */}
              <Route 
                path="/staff" 
                element={
                  <StaffRoute>
                    <StaffLayout />
                  </StaffRoute>
                }
              >
                <Route index element={<StaffDashboard />} />
                <Route path="settings" element={<StaffSettings />} />
                <Route path="account" element={<StaffAccount />} />
              </Route>
              
              {/* Catch all - redirect to landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
